/**
 * Financials Math Utilities
 * Isolated math functions for the Financials feature
 */

import { DateTime } from "luxon";
import {
  assignShowToBucket,
  UNASSIGNED_LABEL,
  sortFinancialSlotLabels,
} from "../../../../lib/barTimeSlots";
import {
  normalizeBarTimeZone,
  parseCivilDateOnly,
  combineCivilDateAndLocalTimes,
} from "../../../../lib/civilDateTime.js";
import { formatDateForInput } from "../../../utils/calendarDate";

export { sortFinancialSlotLabels };

/** “Now” interpreted in the venue time zone (for week/month boundaries). */
export const venueNowInBarZone = (barTimeZone, ref = new Date()) =>
  DateTime.fromJSDate(ref).setZone(normalizeBarTimeZone(barTimeZone));

/** Monday–today (inclusive civil dates) in venue zone. */
export const venueWeekRangeYmd = (barTimeZone, ref = new Date()) => {
  const today = venueNowInBarZone(barTimeZone, ref).startOf("day");
  const monday = today.minus({ days: today.weekday - 1 });
  return { startYmd: monday.toISODate(), endYmd: today.toISODate() };
};

/** Previous calendar week Mon–Sun in venue zone. */
export const venuePreviousFullWeekRangeYmd = (barTimeZone, ref = new Date()) => {
  const thisWeekStart = venueWeekRangeYmd(barTimeZone, ref).startYmd;
  const monday = DateTime.fromISO(thisWeekStart).minus({ weeks: 1 });
  const sunday = monday.plus({ days: 6 });
  return { startYmd: monday.toISODate(), endYmd: sunday.toISODate() };
};

/** Same weekday offset as current week, last week (partial week compare). */
export const venuePreviousWeekPartialRangeYmd = (
  barTimeZone,
  ref = new Date(),
) => {
  const tz = normalizeBarTimeZone(barTimeZone);
  const today = venueNowInBarZone(tz, ref).startOf("day");
  const monday = today.minus({ days: today.weekday - 1 });
  const prevMonday = monday.minus({ weeks: 1 });
  const prevToday = today.minus({ weeks: 1 });
  return {
    startYmd: prevMonday.toISODate(),
    endYmd: prevToday.toISODate(),
    referenceInstant: DateTime.fromJSDate(ref).setZone(tz).minus({ weeks: 1 }).toJSDate(),
  };
};

export const venueMonthRangeYmd = (barTimeZone, ref = new Date()) => {
  const today = venueNowInBarZone(barTimeZone, ref).startOf("day");
  return {
    startYmd: today.startOf("month").toISODate(),
    endYmd: today.toISODate(),
  };
};

/** Same day-of-month offset, previous month (matches prior dashboard logic). */
export const venuePreviousMonthPartialRangeYmd = (
  barTimeZone,
  ref = new Date(),
) => {
  const tz = normalizeBarTimeZone(barTimeZone);
  const today = venueNowInBarZone(tz, ref).startOf("day");
  const prevToday = today.minus({ months: 1 });
  return {
    startYmd: prevToday.startOf("month").toISODate(),
    endYmd: prevToday.toISODate(),
    referenceInstant: DateTime.fromJSDate(ref).setZone(tz).minus({ months: 1 }).toJSDate(),
  };
};

export const venueYearRangeYmd = (barTimeZone, ref = new Date()) => {
  const today = venueNowInBarZone(barTimeZone, ref).startOf("day");
  return {
    startYmd: today.startOf("year").toISODate(),
    endYmd: today.toISODate(),
  };
};

/** Same calendar offset, previous year. */
export const venuePreviousYearPartialRangeYmd = (
  barTimeZone,
  ref = new Date(),
) => {
  const tz = normalizeBarTimeZone(barTimeZone);
  const today = venueNowInBarZone(tz, ref).startOf("day");
  const prevToday = today.minus({ years: 1 });
  return {
    startYmd: prevToday.startOf("year").toISODate(),
    endYmd: prevToday.toISODate(),
    referenceInstant: DateTime.fromJSDate(ref).setZone(tz).minus({ years: 1 }).toJSDate(),
  };
};

export const unionSlotLabels = (brA, brB) => {
  const set = new Set([
    ...Object.keys(brA?.breakdown || {}),
    ...Object.keys(brB?.breakdown || {}),
  ]);
  return sortFinancialSlotLabels([...set]);
};

/**
 * Sales grouped by per-day slot labels (e.g. "Prime · Sat") from bar time_slots JSON.
 */
export const calculateSalesBySlotLabels = (shows, timeSlotsRaw) => {
  const breakdown = {};
  if (!shows || !Array.isArray(shows) || shows.length === 0) {
    return { breakdown: {}, total: 0, orderedLabels: [] };
  }
  shows.forEach((show) => {
    const { label } = assignShowToBucket(show, timeSlotsRaw);
    const key = label || UNASSIGNED_LABEL;
    if (!breakdown[key]) breakdown[key] = { sales: 0, count: 0 };
    breakdown[key].sales += parseFloat(show.sales) || 0;
    breakdown[key].count += 1;
  });
  const total = Object.values(breakdown).reduce((s, x) => s + x.sales, 0);
  const orderedLabels = sortFinancialSlotLabels(Object.keys(breakdown));
  return { breakdown, total, orderedLabels };
};

export { UNASSIGNED_LABEL };

/**
 * Get current date and time
 * @returns {Date} - Current date and time
 */
export const getCurrentDateTime = () => {
  return new Date();
};

/**
 * Get Monday of the week for a given date
 * @param {Date} date - Date to get week start for
 * @returns {Date} - Monday of that week (00:00:00)
 */
export const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const diff = day === 0 ? 6 : day - 1; // Days to subtract to get to Monday
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get 1st of month for a given date
 * @param {Date} date - Date to get month start for
 * @returns {Date} - 1st of that month (00:00:00)
 */
export const getMonthStart = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get January 1st for a given date's year
 * @param {Date} date - Date to get year start for
 * @returns {Date} - January 1st of that year (00:00:00)
 */
export const getYearStart = (date) => {
  const d = new Date(date);
  d.setMonth(0, 1); // January (0-indexed), day 1
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Calculate previous period dates (same day/time in previous period)
 * @param {Date} currentStart - Start date of current period
 * @param {Date} currentEnd - End date/time of current period
 * @param {string} periodType - 'week', 'month', or 'year'
 * @returns {Object} - { start: Date, end: Date }
 */
export const getPreviousPeriodDates = (
  currentStart,
  currentEnd,
  periodType
) => {
  const previousStart = new Date(currentStart);
  const previousEnd = new Date(currentEnd);

  if (periodType === "week") {
    // Previous week: subtract 7 days
    previousStart.setDate(previousStart.getDate() - 7);
    previousEnd.setDate(previousEnd.getDate() - 7);
  } else if (periodType === "month") {
    // Previous month: subtract 1 month
    previousStart.setMonth(previousStart.getMonth() - 1);
    previousEnd.setMonth(previousEnd.getMonth() - 1);
  } else if (periodType === "year") {
    // Previous year: subtract 1 year
    previousStart.setFullYear(previousStart.getFullYear() - 1);
    previousEnd.setFullYear(previousEnd.getFullYear() - 1);
  }

  return {
    start: previousStart,
    end: previousEnd,
  };
};

/**
 * Filter shows by inclusive civil date range (YYYY-MM-DD) and whether the show
 * has ended before referenceInstant, using venue-local wall times for end-of-show.
 */
export const filterShowsByDateAndTime = (
  shows,
  rangeStartYmd,
  rangeEndYmd,
  referenceInstant,
  barTimeZone,
) => {
  if (!shows || !Array.isArray(shows)) return [];
  if (
    !rangeStartYmd ||
    !rangeEndYmd ||
    !referenceInstant ||
    barTimeZone == null
  ) {
    return [];
  }

  const tz = normalizeBarTimeZone(barTimeZone);

  return shows.filter((show) => {
    if (!show.date) return false;
    const ymd = parseCivilDateOnly(formatDateForInput(show.date));
    if (!ymd || ymd < rangeStartYmd || ymd > rangeEndYmd) return false;

    const end = combineCivilDateAndLocalTimes(
      ymd,
      show.start_time,
      show.end_time,
      tz,
    );
    return !!(end && end.getTime() < referenceInstant.getTime());
  });
};

/**
 * Calculate total sales from filtered shows
 * @param {Array} shows - Array of show objects with sales field
 * @returns {number} - Total sales amount
 */
export const calculateTotalSales = (shows) => {
  if (!shows || !Array.isArray(shows)) return 0;
  return shows.reduce((total, show) => {
    return total + (parseFloat(show.sales) || 0);
  }, 0);
};

/**
 * Calculate percentage change between two values
 * @param {number} current - Current period value
 * @param {number} previous - Previous period value
 * @returns {Object} - { percentage: number, isPositive: boolean }
 */
export const calculatePercentageChange = (current, previous) => {
  // Ensure we're working with numbers
  const currentNum =
    typeof current === "number" ? current : parseFloat(current) || 0;
  const previousNum =
    typeof previous === "number" ? previous : parseFloat(previous) || 0;

  if (previousNum === 0) {
    // If previous is 0, show 0% if current is also 0, or 100% if current > 0
    return {
      percentage: currentNum === 0 ? 0 : 100,
      isPositive: currentNum >= 0,
    };
  }

  const percentage = ((currentNum - previousNum) / previousNum) * 100;
  return {
    percentage: Math.round(percentage * 100) / 100, // Round to 2 decimals
    isPositive: percentage >= 0,
  };
};

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format percentage with sign
 * @param {number} percentage - Percentage value
 * @param {boolean} isPositive - Whether the change is positive
 * @returns {string} - Formatted percentage string with sign
 */
export const formatPercentage = (percentage, isPositive) => {
  const sign = isPositive ? "+" : "";
  return `${sign}${percentage.toFixed(2)}%`;
};

/**
 * Format date range (e.g., "1/11/26 - 11/13/26")
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} - Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "";

  const formatDate = (date) => {
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear().toString().slice(-2); // Last 2 digits of year
    return `${month}/${day}/${year}`;
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Filter shows by artist ID
 * @param {Array} shows - Array of show objects
 * @param {number} artistId - Artist ID to filter by
 * @returns {Array} - Filtered shows array
 */
export const filterShowsByArtist = (shows, artistId) => {
  if (!shows || !Array.isArray(shows)) return [];
  if (!artistId) return shows;
  return shows.filter((show) => show.artist_id === artistId);
};

/**
 * Convert time string to minutes for comparison
 * @param {string} timeStr - Time string (HH:MM:SS or HH:MM)
 * @returns {number} - Minutes since midnight
 */
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

/**
 * Map a show to the closest time slot based on start_time comparison
 * @param {Object} show - Show object with start_time
 * @param {Array} timeSlots - Array of time slot objects with start_time and name
 * @returns {Object|null} - The closest time slot or null if no slots provided
 */
export const mapShowToTimeSlot = (show, timeSlots) => {
  if (!show || !show.start_time || !timeSlots || timeSlots.length === 0) {
    return null;
  }

  const showMinutes = timeToMinutes(show.start_time);

  // Find closest time slot
  const closestSlot = timeSlots.reduce(
    (closest, slot) => {
      if (!slot.start_time) return closest;

      const slotMinutes = timeToMinutes(slot.start_time);
      const diff = Math.abs(showMinutes - slotMinutes);

      // Handle overnight slots (e.g., Late slot ending at 01:00:00)
      // Check if wrapping around midnight would be closer
      const overnightDiff = Math.abs(showMinutes - (slotMinutes + 24 * 60));
      const reverseOvernightDiff = Math.abs(
        showMinutes + 24 * 60 - slotMinutes
      );
      const minDiff = Math.min(diff, overnightDiff, reverseOvernightDiff);

      return minDiff < closest.diff ? { slot, diff: minDiff } : closest;
    },
    { slot: null, diff: Infinity }
  );

  return closestSlot.slot;
};

/**
 * Calculate sales breakdown by time slot
 * @param {Array} shows - Array of show objects with start_time and sales
 * @param {Array} timeSlots - Array of time slot objects with start_time and name
 * @returns {Object} - Object with sales by time slot name and total
 */
export const calculateSalesByTimeSlot = (shows, timeSlots) => {
  if (!shows || !Array.isArray(shows) || shows.length === 0) {
    // Return empty breakdown for all time slots
    const breakdown = {};
    if (timeSlots && Array.isArray(timeSlots)) {
      timeSlots.forEach((slot) => {
        if (slot.name) {
          breakdown[slot.name] = { sales: 0, count: 0 };
        }
      });
    }
    return { ...breakdown, total: 0 };
  }

  if (!timeSlots || !Array.isArray(timeSlots) || timeSlots.length === 0) {
    // If no time slots, just return total
    const total = calculateTotalSales(shows);
    return { total };
  }

  // Initialize breakdown for all time slots
  const breakdown = {};
  timeSlots.forEach((slot) => {
    if (slot.name) {
      breakdown[slot.name] = { sales: 0, count: 0 };
    }
  });

  // Map shows to time slots and calculate sales
  shows.forEach((show) => {
    const slot = mapShowToTimeSlot(show, timeSlots);
    if (slot && slot.name) {
      const sales = parseFloat(show.sales) || 0;
      if (!breakdown[slot.name]) {
        breakdown[slot.name] = { sales: 0, count: 0 };
      }
      breakdown[slot.name].sales += sales;
      breakdown[slot.name].count += 1;
    }
  });

  // Calculate total
  const total = Object.values(breakdown).reduce(
    (sum, slotData) => sum + (slotData.sales || 0),
    0
  );

  return { ...breakdown, total };
};
